import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { reportService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { reduceEntityDelta } from '@/stores/helpers/delta';
import { Dashboard, Report } from '@/types';

import { normalizeReportDashboard } from '../normalization';
import { ReportActions, ReportState } from '../types';

const patchReportCollections = (
    reports: Report[],
    selectedReport: Report | null,
    id: string,
    updates: Partial<Report>,
) => ({
    reports: mergeEntityById(reports, id, updates),
    selectedReport: selectedReport?.id === id ? mergeEntity(selectedReport, updates) : selectedReport,
});

export const createReportActions: StateCreator<
    ReportState & ReportActions,
    [['zustand/immer', never]],
    [],
    ReportActions
> = (set, get) => ({
    // Core
    fetchReports: async (params) => {
        set({ loading: true });

        try {
            const reports = await reportService.list(params);

            set({ reports, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load reports' });
        }
    },
    fetchReportById: async (id) => {
        set({ loadingReport: true, error: undefined });

        try {
            const report = await reportService.getById(id);
            const existingReport = get().reports.find((currentReport) => currentReport.id === id);
            const normalizedReport = normalizeReportDashboard(report, existingReport?.dashboard);

            set({
                reports: upsertEntityById(get().reports, normalizedReport),
                selectedReport: normalizedReport,
                loadingReport: false,
            });
        } catch (error) {
            captureException(error);

            set({ error: 'Failed to fetch report detail', loadingReport: false });
        }
    },
    clearSelectedReport: () => {
        set({ selectedReport: null });
    },
    createReport: async (input) =>
        runSingleFlight(createSingleFlightKey('reports:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as Report;

            set({ reports: appendEntity(get().reports, optimistic) });

            try {
                const report = await reportService.create(input);
                const normalizedReport = normalizeReportDashboard(report, input.dashboard);

                set({ reports: replaceOptimisticEntityById(get().reports, tempId, normalizedReport) });

                return normalizedReport;
            } catch (error) {
                captureException(error);

                set({ reports: removeEntityById(get().reports, tempId), error: 'Failed to create report' });
            }
        }),
    updateReport: async (id, updates) => {
        const prev = get().reports.find((f) => f.id === id);
        if (!prev) {
            return;
        }

        set({
            reports: mergeEntityById(get().reports, id, updates),
            selectedReport:
                get().selectedReport?.id === id ? mergeEntity(get().selectedReport!, updates) : get().selectedReport,
        });

        try {
            const report = await reportService.update(id, updates);

            set({
                reports: mergeEntityById(get().reports, id, report),
                selectedReport:
                    get().selectedReport?.id === id ? mergeEntity(get().selectedReport!, report) : get().selectedReport,
            });
        } catch (error) {
            captureException(error);

            set({
                reports: replaceEntityById(get().reports, id, prev),
                selectedReport: get().selectedReport?.id === id ? prev : get().selectedReport,
                error: 'Failed to update report',
            });
        }
    },
    deleteReport: async (id) => {
        const prev = get().reports;

        set({
            reports: removeEntityById(get().reports, id),
            selectedReport: get().selectedReport?.id === id ? null : get().selectedReport,
        });

        try {
            await reportService.delete(id);
        } catch (error) {
            captureException(error);

            set({ reports: prev, error: 'Failed to delete report' });
        }
    },
    applyReportDelta: (packet) => {
        if (packet.entity !== 'report') {
            return;
        }

        const next = reduceEntityDelta<Report>(get().reports, get().selectedReport, packet);

        if (!next) {
            return;
        }

        set({
            reports: next.items,
            selectedReport: next.selected,
        });
    },
    // - Aliases
    setReportsFromDashboard: (reports: Report[], dashboard?: Dashboard) => {
        set({ reports: reports.map((report) => normalizeReportDashboard(report, dashboard)) });
    },
    duplicateReport: async (id) => {
        const prev = get().reports;

        if (!prev) {
            return;
        }

        try {
            const duplicated = await reportService.duplicate(id);

            set({ reports: upsertEntityById(get().reports, duplicated, { prepend: true }) });

            toast(`Report Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.name,
                resource: {
                    id: duplicated.id,
                    parentId: duplicated.dashboard?.id,
                    type: 'report',
                },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ reports: prev, error: 'Failed to duplicate report' });

            toast.error('Failed to duplicate report');
        }
    },
    toggleReportFavorite: async (id, favorited) => {
        const prev = get().reports.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set(patchReportCollections(get().reports, get().selectedReport, id, { favorited }));

        try {
            await reportService.setFavorite(id, favorited);

            toast(`${favorited ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(favorited ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, parentId: prev.dashboard?.id, type: 'report' },
            });
        } catch (error) {
            captureException(error);

            set({
                ...patchReportCollections(get().reports, get().selectedReport, id, {
                    favorited: prev.favorited,
                }),
                error: 'Failed to update favorite',
            });
        }
    },
    toggleReportLock: async (id, locked) => {
        const prev = get().reports.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set(patchReportCollections(get().reports, get().selectedReport, id, { locked }));

        try {
            await reportService.setLocked(id, locked);

            toast(`Report ${locked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(locked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.name,
                resource: { id, parentId: prev.dashboard?.id, type: 'report' },
            });
        } catch (error) {
            captureException(error);

            set({
                ...patchReportCollections(get().reports, get().selectedReport, id, {
                    locked: prev.locked,
                }),
                error: 'Failed to update lock',
            });
        }
    },
    toggleReportArchive: async (id, archived) => {
        const prev = get().reports.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set(patchReportCollections(get().reports, get().selectedReport, id, { archived }));

        try {
            await reportService.setArchived(id, archived);

            toast(`Report ${archived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(archived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, parentId: prev.dashboard?.id, type: 'report' },
            });
        } catch (error) {
            captureException(error);

            set({
                ...patchReportCollections(get().reports, get().selectedReport, id, {
                    archived: prev.archived,
                }),
                error: 'Failed to update favorite',
            });
        }
    },
    createComponentInReport: async (reportId, input) => {
        // const report = state.selectedReport;

        // if (!report) {
        //     captureException('No selected report when creating component.');
        // }

        // Optimistic component
        const tempId = `temp-${Date.now()}`;

        // set((state) => {
        //     if (!state.selectedReport?.components) {
        //         state.selectedReport!.components = [];
        //     }

        //     state.selectedReport!.components.push({ id: tempId, ...input } as ReportComponent);
        // });

        try {
            const fullUpdatedReport = await reportService.createComponent(reportId, input);
            const existingReport =
                get().reports.find((report) => report.id === fullUpdatedReport.id) ??
                (get().selectedReport?.id === fullUpdatedReport.id ? get().selectedReport : undefined);
            const normalizedReport = normalizeReportDashboard(fullUpdatedReport, existingReport?.dashboard);

            // Replace optimistic component & update full report
            set({
                selectedReport: normalizedReport.id ? normalizedReport : get().selectedReport,
                reports: get().reports.some((report) => report.id === normalizedReport.id)
                    ? replaceEntityById(get().reports, normalizedReport.id, normalizedReport)
                    : get().reports,
            });

            return normalizedReport;
        } catch (error) {
            captureException(error);

            // Rollback
            set({
                selectedReport: get().selectedReport?.components
                    ? {
                          ...get().selectedReport!,
                          components: get().selectedReport!.components.filter((component) => component.id !== tempId),
                      }
                    : get().selectedReport,
                error: 'Failed to create component in report',
            });
        }
    },
    // Helper
    reorderReports: (ordered: Report[]) => set({ reports: ordered }),
});
